import { useCallback } from 'react';
import { EventTypeDto, ScheduledEventDto } from '@act/shared/data-transfer-objects';
import { scheduledEventRequests } from 'redux/requests/scheduled-event.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import type { NoteFieldSaveOverride } from '../NoteField';
import { useParentLinkedNoteSaveOverride } from './useParentLinkedNoteSaveOverride.hook';

/** Params for {@link useScheduledEventNoteSaveOverride}. */
export interface UseScheduledEventNoteSaveOverrideParams {
  userId: string;
  scheduledEvent?: ScheduledEventDto;
}

/**
 * Debounced scheduled-event note autosave via parent PATCH (`updateScheduledEvent` with `{ note, type }`).
 *
 * @param params - Member id and existing scheduled event (edit mode only).
 * @returns Save override for {@link NoteField}.
 */
export function useScheduledEventNoteSaveOverride({
  userId,
  scheduledEvent,
}: UseScheduledEventNoteSaveOverrideParams): {
  scheduledEventNoteSaveOverride: NoteFieldSaveOverride | undefined;
} {
  const [
    _noteAutosaveResult,
    isNoteAutosaving,
    saveScheduledEventNoteOnly,
    noteAutosaveError,
  ] = useApiCall(scheduledEventRequests.updateScheduledEvent);

  const saveScheduledEventNoteViaParent = useCallback(
    (text: string) => {
      if (scheduledEvent?.id == null) {
        return;
      }
      saveScheduledEventNoteOnly({
        params: { userId, scheduledEventId: scheduledEvent.id },
        body: { note: text, type: scheduledEvent.type as EventTypeDto },
      });
    },
    [saveScheduledEventNoteOnly, scheduledEvent?.id, scheduledEvent?.type, userId]
  );

  const scheduledEventNoteSaveOverride = useParentLinkedNoteSaveOverride({
    enabled: scheduledEvent?.id != null,
    onSaveNote: saveScheduledEventNoteViaParent,
    isSaving: isNoteAutosaving,
    error: noteAutosaveError,
  });

  return { scheduledEventNoteSaveOverride };
}
